import { resolveHeaderI18nKey } from './pageHeaders';
import { resolveUiI18nKey } from './uiStrings';

type TranslateFn = (key: string, fallback?: string) => string;

/** Resolve page headers, table titles, field labels, buttons, and confirm copy. */
export function translateUiText(text: string, t: TranslateFn): string {
  const trimmed = text.trim();
  if (!trimmed) return trimmed;

  const rental = trimmed.match(/^Rental Packages — (.+)$/i);
  if (rental) {
    return `${t('admin.page.rental_packages_zone', 'Rental Packages')} — ${rental[1]}`;
  }

  const outstation = trimmed.match(/^Outstation Pricing — (.+)$/i);
  if (outstation) {
    return `${t('admin.page.outstation_pricing_zone', 'Outstation Pricing')} — ${outstation[1]}`;
  }

  const ridesMatch = trimmed.match(/^(\d+)\s+ride\(s\) matching filters$/i);
  if (ridesMatch) {
    return `${ridesMatch[1]} ${t('admin.table.rides_matching', 'ride(s) matching filters')}`;
  }

  const rowsOf = trimmed.match(/^(\d+)\s+of\s+(\d+)\s+rows$/i);
  if (rowsOf) {
    return `${rowsOf[1]} ${t('admin.grid.of', 'of')} ${rowsOf[2]} ${t('admin.grid.rows', 'rows')}`;
  }

  const rowsOnly = trimmed.match(/^(\d+)\s+rows$/i);
  if (rowsOnly) {
    return `${rowsOnly[1]} ${t('admin.grid.rows', 'rows')}`;
  }

  const inLabel = trimmed.match(/^(\d+)\s+in\s+(.+)$/i);
  if (inLabel) {
    const label = translateUiText(inLabel[2], t);
    return `${inLabel[1]} ${t('admin.table.in', 'in')} ${label}`;
  }

  if (/^\d+\s+sub-service/i.test(trimmed)) {
    return trimmed
      .replace(
        'sub-service(s) · open to manage operations',
        t('admin.header.sub_services_open', 'sub-service(s) · open to manage operations'),
      )
      .replace(
        'sub-service(s) · add custom Food/Grocery flows',
        t('admin.header.sub_services_add', 'sub-service(s) · add custom Food/Grocery flows'),
      );
  }

  const addEdit = trimmed.match(/^(Add|Edit|Create|Remove|Delete|Deactivate|Activate)\s+(.+)$/i);
  if (addEdit) {
    const whole = resolveUiI18nKey(trimmed);
    if (whole) return t(whole, trimmed);
    const verb = addEdit[1];
    const rest = addEdit[2];
    const verbNorm = verb.charAt(0).toUpperCase() + verb.slice(1).toLowerCase();
    const verbKey = resolveUiI18nKey(verbNorm) || resolveUiI18nKey(verb);
    const verbTranslated = verbKey ? t(verbKey, verb) : verb;
    return `${verbTranslated} ${translateUiText(rest, t)}`;
  }

  const parentDyn = trimmed.match(/^Parent\s+(.+)$/i);
  if (parentDyn) {
    return `${t('admin.form.parent_prefix', 'Parent')} ${translateUiText(parentDyn[1], t)}`;
  }

  // Prefer exact UI catalog keys before page-header slug fallback
  const uiKey = resolveUiI18nKey(trimmed);
  if (uiKey) return t(uiKey, trimmed);

  return t(resolveHeaderI18nKey(trimmed), trimmed);
}
